/**
 * LessonRecapService — the family-facing READ path for Lesson Recaps.
 *
 * Recaps live in `documents` (type 'lesson_note'), the same collection that
 * holds staff planning docs and scripts. That collision is historical and is
 * why every query here constrains BOTH studentId and visibility: a query that
 * constrained neither would be denied outright, and one that constrained only
 * studentId would be denied too — the rule's customer branch requires the
 * visibility predicate to be satisfiable from the query itself.
 *
 * RULES ARE NOT FILTERS. firestore.rules → match /documents/ customer branch:
 *
 *   isCustomer() && myFamilyId() != null
 *     && resource.data.studentId != null
 *     && resource.data.visibility in ['student','parent']
 *     && get(students/$(studentId)).data.familyId == myFamilyId()
 *
 * A client query that does not itself constrain `visibility` to those two
 * values is DENIED WHOLESALE — not trimmed to the readable subset. The list
 * would render empty and look like "no recaps yet", which is the failure mode
 * this comment exists to prevent.
 *
 * ONE QUERY PER STUDENT, deliberately — the same shape SongNotesService uses.
 * Family membership is resolved by the RULE, through a get() on
 * students/{studentId}, never denormalized onto the recap: a copied familyId
 * goes stale the day a family merges, and familyResolution.js really does merge
 * families (status:'merged' + mergedIntoFamilyId). Constraining to a single
 * studentId also collapses that get() to ONE document access regardless of how
 * many recaps come back — Firestore counts access per unique PATH, so every
 * recap in a single-student query resolves the same students/{id}.
 *
 * ⚠ REQUIRES A COMPOSITE INDEX: (studentId ASC, visibility ASC, createdAt DESC).
 * Committed in firestore.indexes.json alongside this file and it MUST ship in
 * the same deploy. The Firestore emulator serves this query without the index,
 * so every jest and rules test passes green while production returns 400 on the
 * first real read. An index is a deploy-checklist item, not a testable one.
 */

import { db } from '../firebase';
import { collection, query, where, orderBy, limit as fsLimit, getDocs } from 'firebase/firestore';
import { visibilitiesFor } from './lessonRecapModel';

const COLLECTION = 'documents';

/**
 * Every recap for one student that this family may read, newest first.
 *
 * @param {string} studentId - the child whose recaps to load
 * @param {number} max - hard cap; a season is ~35 lessons × 2 tiers, so 200
 *                       covers a full year with room and bounds a runaway read.
 * @param {{studentOnly?: boolean}} opts - true for a STUDENT principal, whose
 *                       rule admits the `student` tier only (see visibilitiesFor).
 *                       The same composite index serves both shapes.
 */
export const fetchFamilyRecapsForStudent = async (studentId, max = 200, { studentOnly = false } = {}) => {
	if (!studentId) return [];

	const q = query(
		collection(db, COLLECTION),
		where('studentId', '==', studentId),
		// Load-bearing: see the rules-are-not-filters note above.
		where('visibility', 'in', visibilitiesFor({ studentOnly })),
		orderBy('createdAt', 'desc'),
		fsLimit(max)
	);

	const snapshot = await getDocs(q);
	return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
};

const LessonRecapService = { fetchFamilyRecapsForStudent };

export default LessonRecapService;
